import * as React from 'react'
import { CheckIcon, ChevronDown, Minus, X, Globe } from 'lucide-react'
import { CircleFlag } from 'react-circle-flags'

import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  ALL_COUNTRIES,
  REGION_GROUPS,
  getCountryLabel,
  getFullySelectedRegions,
  getIndividualCountries,
  isRegionFullySelected,
  isRegionPartiallySelected,
  toggleRegion,
} from '@/constants/countries'

export interface CountryRegionSelectProps {
  /** Currently selected ISO alpha-2 country codes */
  value: string[]
  /** Callback when selection changes */
  onChange: (value: string[]) => void
  /** Whether the component is disabled */
  disabled?: boolean
  /** Placeholder when no items selected */
  placeholder?: string
  /** Additional class names */
  className?: string
  /** ID for accessibility */
  id?: string
}

/**
 * A multi-select component for selecting countries and regions.
 * Supports region shortcuts (e.g., "European Union") that expand to individual countries.
 */
const CountryRegionSelect = React.forwardRef<HTMLButtonElement, CountryRegionSelectProps>(
  (
    {
      value,
      onChange,
      disabled = false,
      placeholder = 'Select countries...',
      className,
      id,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false)

    const handleToggleCountry = React.useCallback(
      (code: string) => {
        const upperCode = code.toUpperCase()
        if (value.includes(upperCode)) {
          onChange(value.filter((v) => v !== upperCode))
        } else {
          onChange([...value, upperCode])
        }
      },
      [value, onChange]
    )

    const handleToggleRegion = React.useCallback(
      (regionValue: string) => {
        const region = REGION_GROUPS.find((r) => r.value === regionValue)
        if (region) {
          onChange(toggleRegion(region, value))
        }
      },
      [value, onChange]
    )

    const handleRemove = React.useCallback(
      (code: string, e: React.MouseEvent) => {
        e.stopPropagation()
        onChange(value.filter((v) => v !== code))
      },
      [value, onChange]
    )

    const handleRemoveRegion = React.useCallback(
      (regionValue: string, e: React.MouseEvent) => {
        e.stopPropagation()
        const region = REGION_GROUPS.find((r) => r.value === regionValue)
        if (region) {
          // Remove all countries in this region
          onChange(value.filter((c) => !region.countries.includes(c)))
        }
      },
      [value, onChange]
    )

    // Compute display badges: show regions for fully-selected regions,
    // and individual countries for the rest
    const fullySelectedRegions = React.useMemo(
      () => getFullySelectedRegions(value),
      [value]
    )
    const individualCountries = React.useMemo(
      () => getIndividualCountries(value),
      [value]
    )

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          ref={ref}
          id={id}
          disabled={disabled}
          className={cn(
            'flex min-h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
        >
          <div className="flex flex-wrap gap-1 flex-1">
            {value.length > 0 ? (
              <>
                {/* Show region badges for fully-selected regions */}
                {fullySelectedRegions.map((region) => (
                  <Badge
                    key={region.value}
                    variant="secondary"
                    className="flex items-center gap-1 pr-1"
                  >
                    <Globe className="h-3 w-3" />
                    {region.label}
                    {!disabled && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => handleRemoveRegion(region.value, e)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            handleRemoveRegion(region.value, e as unknown as React.MouseEvent)
                          }
                        }}
                        className="ml-1 rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10 cursor-pointer"
                        aria-label={`Remove ${region.label}`}
                      >
                        <X className="h-3 w-3" />
                      </span>
                    )}
                  </Badge>
                ))}
                {/* Show individual country badges */}
                {individualCountries.map((code) => (
                  <Badge
                    key={code}
                    variant="secondary"
                    className="flex items-center gap-1 pr-1"
                  >
                    <span className="inline-flex items-center justify-center w-4 h-4 shrink-0 overflow-hidden rounded-full">
                      <CircleFlag countryCode={code.toLowerCase()} height={14} />
                    </span>
                    {getCountryLabel(code)}
                    {!disabled && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => handleRemove(code, e)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            handleRemove(code, e as unknown as React.MouseEvent)
                          }
                        }}
                        className="ml-1 rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10 cursor-pointer"
                        aria-label={`Remove ${getCountryLabel(code)}`}
                      >
                        <X className="h-3 w-3" />
                      </span>
                    )}
                  </Badge>
                ))}
              </>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
        </PopoverTrigger>

        <PopoverContent
          collisionPadding={10}
          side="bottom"
          className="min-w-[--radix-popper-anchor-width] p-0"
        >
          <Command className="w-full max-h-[300px]">
            <div className="sticky top-0 z-10 bg-popover">
              <CommandInput placeholder="Search countries..." />
            </div>
            <CommandList>
              <CommandEmpty>No countries found.</CommandEmpty>

              {/* Regions Group */}
              <CommandGroup heading="Regions">
                {REGION_GROUPS.map((region) => {
                  const isFullySelected = isRegionFullySelected(region, value)
                  const isPartiallySelected = isRegionPartiallySelected(region, value)
                  return (
                    <CommandItem
                      key={region.value}
                      onSelect={() => handleToggleRegion(region.value)}
                      className="flex items-center gap-2"
                    >
                      <div
                        className={cn(
                          'flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                          isFullySelected
                            ? 'bg-primary text-primary-foreground'
                            : isPartiallySelected
                              ? 'bg-primary/50 text-primary-foreground'
                              : 'opacity-50'
                        )}
                      >
                        {isFullySelected && <CheckIcon className="h-3 w-3" />}
                        {isPartiallySelected && <Minus className="h-3 w-3" />}
                      </div>
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      <span>{region.label}</span>
                      <span className="text-xs text-muted-foreground ml-auto">
                        {region.countries.length} countries
                      </span>
                    </CommandItem>
                  )
                })}
              </CommandGroup>

              <CommandSeparator />

              {/* Countries Group */}
              <CommandGroup heading="Countries">
                {ALL_COUNTRIES.map((country) => {
                  const isSelected = value.includes(country.value)
                  return (
                    <CommandItem
                      key={country.value}
                      onSelect={() => handleToggleCountry(country.value)}
                      className="flex items-center gap-2"
                    >
                      <div
                        className={cn(
                          'flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                          isSelected
                            ? 'bg-primary text-primary-foreground'
                            : 'opacity-50'
                        )}
                      >
                        {isSelected && <CheckIcon className="h-3 w-3" />}
                      </div>
                      <span className="inline-flex items-center justify-center w-5 h-5 shrink-0 overflow-hidden rounded-full">
                        <CircleFlag
                          countryCode={country.value.toLowerCase()}
                          height={18}
                        />
                      </span>
                      <span>{country.label}</span>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  }
)

CountryRegionSelect.displayName = 'CountryRegionSelect'

export { CountryRegionSelect }
